import { ComponentType } from '@angular/cdk/overlay';
import { Injectable, Type, inject } from '@angular/core';

import { AppConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollFreezeService, ScrollFreezeServiceTypes } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DeviceService, SessionStoreService } from '@frontend/vanilla/core';
import { extend } from 'lodash-es';

import { Modal } from '../common/modal';
import { ModalRef } from '../common/modal-ref';
import { MessageDialogComponent } from './message-dialog.component';
import { ModalDialogComponent } from './modal-dialog.component';
import { ContentData, DialogAnimation, DialogSettings, ModalDialogOptions, UnwrapComponentProps } from './modal-dialog.model';
import { ModalEventBridgeService } from './modal-event-bridge.service';

@Injectable({ providedIn: 'root' })
export class ModalDialogService {
    static get DefaultSettings(): DialogSettings {
        return {
            isModalDialog: true,
            showDialogHeader: false,
            showPageHeader: false,
            fit: true,
            hideCloseButton: false,
        };
    }

    private openModalsCountKey = 'openedSportsModalDialogsCount';
    private animationClasses: Record<DialogAnimation, string> = {
        [DialogAnimation.SlideInFromBottom]: 'modal-slide-from-bottom',
        [DialogAnimation.SlideInFromRight]: 'modal-slide-from-right',
        [DialogAnimation.SlideOutFromTop]: 'modal-slide-out-to-bottom',
    };

    private modal: Modal = inject(Modal);
    private scrollFreeze: ScrollFreezeService = inject(ScrollFreezeService);
    private eventBridge: ModalEventBridgeService = inject(ModalEventBridgeService);
    private deviceService: DeviceService = inject(DeviceService);
    private sessionStorage: SessionStoreService = inject(SessionStoreService);
    private appConfig: AppConfig = inject(AppConfig);

    private get openedModalsCount(): number {
        return this.sessionStorage.get<number>(this.openModalsCountKey) ?? 0;
    }

    private increaseModalsCount(): void {
        this.sessionStorage.set<number>(this.openModalsCountKey, this.openedModalsCount + 1);
    }

    private decreaseModalsCount(): void {
        this.sessionStorage.set<number>(this.openModalsCountKey, this.openedModalsCount - 1);
    }

    isModalDialogOpen(): boolean {
        return this.openedModalsCount > 0;
    }

    openPopup<TComponent>(component: Type<TComponent>, optionsParam?: ModalDialogOptions<Partial<UnwrapComponentProps<TComponent>>>): ModalRef {
        let options = optionsParam ?? {};
        options.settings = options.settings ?? {};
        options.settings.isPopup = true;

        return this.openDialog(component, options);
    }

    openDialog<TComponent>(component: Type<TComponent>, optionsParam?: ModalDialogOptions<Partial<UnwrapComponentProps<TComponent>>>): ModalRef {
        let options = optionsParam ?? {};
        const defaultSettings = extend({}, ModalDialogService.DefaultSettings, { fit: !options.settings?.isPopup });
        const modalRef = this.openModal(ModalDialogComponent, defaultSettings, options.settings);

        const dialogInstance = modalRef.componentInstance as ModalDialogComponent;
        dialogInstance.contentComponent = component;
        dialogInstance.inputs = options.data;
        dialogInstance.modalRef = modalRef;
        dialogInstance.dialogSettings = extend({}, ModalDialogService.DefaultSettings, options.settings);

        return modalRef;
    }

    openMessage(contentData: ContentData, settings?: DialogSettings): ModalRef {
        const defaultSettings = extend({}, ModalDialogService.DefaultSettings, { showDialogHeader: false });
        const modalRef = this.openModal(MessageDialogComponent, defaultSettings, settings);

        const dialogInstance = modalRef.componentInstance as MessageDialogComponent;
        dialogInstance.contentData = contentData;

        return modalRef;
    }

    // eslint-disable-next-line max-lines-per-function
    private openModal<TComponent extends ComponentType<unknown>>(
        component: TComponent,
        defaultSettings: DialogSettings,
        settings?: DialogSettings,
    ): ModalRef {
        const copySettings = extend({}, defaultSettings, settings);
        const modalCss = this.buildModalCss(copySettings, settings?.isPopup);
        const modalBackdropCss = this.buildModalBackdropCss(copySettings, settings?.isPopup);

        if (copySettings.closeAnimation) {
            copySettings.closeAnimation = this.animationClasses[copySettings.closeAnimation] as DialogAnimation;
        }

        const modalRef = this.modal.open(component, {
            windowClass: modalCss,
            backdropClass: modalBackdropCss,
            backdrop: copySettings.isModalDialog ? 'static' : true,
            keyboard: !copySettings.isModalDialog,
            container: copySettings.container,
            centerVerticalToViewport: !!copySettings.container,
            beforeDismiss: copySettings.beforeDismiss,
            showDialogHeader: copySettings.showDialogHeader,
            hideCloseButton: copySettings.hideCloseButton,
            title: copySettings.title,
            closeAnimation: copySettings.closeAnimation,
            injector: copySettings.injector,
            epcotShowBalance: copySettings.epcotShowBalance,
            headerTemplate: copySettings.headerTemplate,
            showCloseBtnText: copySettings.showCloseBtnText,
            showBackNav: copySettings.showBackNav,
            closePrevious: copySettings.closePrevious,
            ...(copySettings.animatePopupDismiss === undefined ? {} : { animatePopupDismiss: copySettings.animatePopupDismiss }),
            ...(copySettings.showCloseIcon === undefined ? {} : { showCloseIcon: copySettings.showCloseIcon }),
            gestureOptions: {
                enabled: this.isGesturesEnabled(copySettings),
                headerSelector: copySettings.headerSelector,
                scrollTargetQuerySelector: copySettings.scrollTargetQuerySelector,
                restorePreviousUrl: copySettings.gesturesRestorePreviousUrl,
            },
        });

        if (!this.openedModalsCount) {
            this.eventBridge.notifyOpenOverlay();
        }

        const scrollFreezeType = this.isGesturesEnabled(copySettings) ? ScrollFreezeServiceTypes.None : copySettings.scrollFreezeType;

        this.increaseModalsCount();
        if (copySettings.scrollFreezeType !== ScrollFreezeServiceTypes.None && this.deviceService.isMobile) {
            this.scrollFreeze.freezeScroll(scrollFreezeType);
        }
        modalRef.result.finally(() => {
            if (copySettings.scrollFreezeType !== ScrollFreezeServiceTypes.None && this.deviceService.isMobile) {
                this.scrollFreeze.releaseScroll(scrollFreezeType);
            }
            this.decreaseModalsCount();
            if (!this.openedModalsCount) {
                this.eventBridge.notifyCloseOverlay();
            }

            copySettings.afterResulted?.();
        });

        return modalRef;
    }

    private buildModalBackdropCss(settings: DialogSettings, isPopup?: boolean): string {
        const cssClasses = [];

        if (!isPopup) {
            cssClasses.push('modal-dialog-backdrop');
        }

        if (settings.showPageHeader) {
            cssClasses.push('modal-under-header');
        }

        if (settings.showBottomNav) {
            cssClasses.push('modal-above-footer');
        }

        if (settings.inColumn) {
            cssClasses.push('modal-dialog-backdrop-in-column');
        }

        if (settings.closeAnimation) {
            cssClasses.push('close-animation');
        }

        if (settings.backdropClass) {
            cssClasses.push(settings.backdropClass);
        }

        return cssClasses.filter(Boolean).join(' ');
    }

    private buildModalCss(settings: DialogSettings, isPopup?: boolean): string {
        const cssClasses = [settings.cssClass];

        if (!isPopup) {
            cssClasses.push('modal-dialog-wrapper');
        }

        if (settings.fit) {
            cssClasses.push('modal-fit');
        }

        if (settings.showPageHeader) {
            cssClasses.push('modal-under-header');
        }

        if (settings.showBottomNav) {
            cssClasses.push('modal-above-footer');
        }

        if (settings.animation && !this.isGesturesEnabled(settings)) {
            cssClasses.push(this.animationClasses[settings.animation]);
        }

        if (settings.inColumn) {
            cssClasses.push('modal-dialog-in-column');
        }

        return cssClasses.filter(Boolean).join(' ');
    }

    private isGesturesEnabled(settings: DialogSettings): boolean {
        return Boolean(settings.gestures && this.appConfig.modalDialogGesturesEnabled);
    }
}
